// Admin cache-management vocabulary.
//
// Several features memoize expensive out-of-band lookups (Bedrock model calls and
// the NLM RxNav / openFDA APIs) in global, no-PHI DynamoDB cache partitions so each
// drug is resolved at most once across all patients. Entries normally expire on
// their own TTL, but an admin may want to expire them early — e.g. after switching
// the medication-reference model to a stronger one, so the next lookup re-fetches
// from the better model instead of serving a stale value.
//
// This module is the single source of truth for which caches are clearable and how
// they're labelled; the API maps each id to its DynamoDB partition key (keys.ts),
// and the admin UI renders one button per entry.

export interface CacheKindMeta {
  id: string;
  label: string;
  /** What the cache holds + whether it's model-derived (worth clearing after a model change). */
  description: string;
  /** True when entries come from a Bedrock model (vs. an external API). */
  modelDerived: boolean;
}

export const CACHE_KINDS = [
  {
    id: "drug_reference",
    label: "Medication reference",
    description:
      'Half-lives, dosing, uses & warnings — powers the "still in your system" screen and the dosage popup. Model-derived.',
    modelDerived: true,
  },
  {
    id: "food_guidance",
    label: "Food & timing guidance",
    description: "Take-with-food / empty-stomach guidance shown on medications. Model-derived.",
    modelDerived: true,
  },
  {
    id: "interaction_pairs",
    label: "Drug-interaction verdicts",
    description: "AI verdicts for each drug pair (including checked-but-no-interaction results). Model-derived.",
    modelDerived: true,
  },
  {
    id: "rxcui",
    label: "RxNav drug-ID lookups",
    description: "Drug-name → RxCUI resolution from the NLM RxNav API.",
    modelDerived: false,
  },
  {
    id: "drug_class",
    label: "Drug-class lookups",
    description: "RxCUI → drug-class mapping from the NLM RxNav API.",
    modelDerived: false,
  },
  {
    id: "gym_howto",
    label: "Exercise how-to instructions",
    description: "Step-by-step instructions shown on the Trainer exercise sheet. Model-derived.",
    modelDerived: true,
  },
  {
    id: "gym_dosing",
    label: "Exercise sets & reps guidance",
    description: "Goal-based sets/reps/progression shown in the Trainer log window. Model-derived.",
    modelDerived: true,
  },
  {
    id: "barcode_product",
    label: "Barcode products",
    description: "UPC → nutrition from Open Food Facts / USDA. Not model-derived.",
    modelDerived: false,
  },
] as const satisfies readonly CacheKindMeta[];

export type CacheKind = (typeof CACHE_KINDS)[number]["id"];

export const CACHE_KIND_IDS: readonly CacheKind[] = CACHE_KINDS.map((c) => c.id);

export function isCacheKind(v: unknown): v is CacheKind {
  return typeof v === "string" && (CACHE_KIND_IDS as readonly string[]).includes(v);
}
